/**
 * Adaptive layout for the app-bar Mail / Files quick-access shortcuts.
 *
 * Each shortcut belongs to one of two logical groups — Mail (PanLuma Inbox,
 * Gmail, Outlook) and Files (PanLuma Files, Google Drive, OneDrive) — and is
 * only present when its backing feature/integration is on (see
 * useMailFilesShortcuts). This module decides HOW the present items render,
 * deciding each group (Mail, Files) independently on its own item count:
 *
 * - A group with exactly one source renders as that source's own brand icon
 *   (e.g. just Gmail → the Gmail icon), navigating directly with no menu.
 * - A group with two or more sources collapses into a single generic dropdown
 *   (Mail ▾ / Files ▾) listing the sources — so the brand icon only appears at
 *   the top level when it is the sole source for its domain.
 *
 * Order is always Mail before Files; a single-item group keeps its one item's
 * declared identity (PanLuma first, then Google, then Microsoft).
 */

export type ShortcutGroup = 'mail' | 'files';

export interface ShortcutItem<I = unknown> {
  /** Stable identity: 'inbox' | 'gmail' | 'outlook' | 'files' | 'drive' | 'onedrive'. */
  key: string;
  group: ShortcutGroup;
  /** i18n key for the item label / tooltip. */
  labelKey: string;
  /** Navigation target. */
  path: string;
  /** Icon node (opaque to the layout logic). */
  icon: I;
  /**
   * Unread count for this source, when it has one (PanLuma Inbox). Undefined
   * for sources without an unread signal (files, and — for now — Gmail/Outlook,
   * which have no unread endpoint). Surfaced as a badge by the renderer; ignored
   * by the layout logic.
   */
  unreadCount?: number;
}

/** Total unread across a group's items (sources without a count contribute 0). */
export function groupUnreadTotal<I>(items: ShortcutItem<I>[]): number {
  return items.reduce((sum, item) => sum + (item.unreadCount ?? 0), 0);
}

export type ShortcutUnit<I = unknown> =
  | { kind: 'icon'; item: ShortcutItem<I> }
  | { kind: 'dropdown'; group: ShortcutGroup; triggerLabelKey: string; items: ShortcutItem<I>[] };

/** A group with this many sources (or more) collapses into a generic dropdown. */
export const GROUP_THRESHOLD = 2;

const TRIGGER_LABEL_KEY: Record<ShortcutGroup, string> = {
  mail: 'nav.mail.mail',
  files: 'nav.fileManagement.files',
};

/**
 * Turn the present Mail/Files items into an ordered list of render units
 * (direct icons and/or group dropdowns) per the rules above.
 */
export function computeShortcutLayout<I>(
  mail: ShortcutItem<I>[],
  files: ShortcutItem<I>[],
): ShortcutUnit<I>[] {
  const units: ShortcutUnit<I>[] = [];

  for (const group of ['mail', 'files'] as const) {
    const items = group === 'mail' ? mail : files;
    if (items.length === 0) continue;
    if (items.length >= GROUP_THRESHOLD) {
      units.push({
        kind: 'dropdown',
        group,
        triggerLabelKey: TRIGGER_LABEL_KEY[group],
        items,
      });
    } else {
      for (const item of items) {
        units.push({ kind: 'icon', item });
      }
    }
  }

  return units;
}
